import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useUpdateInventory } from "../../../api/inventory/service/index";
import { useTranslation } from "react-i18next";
import { useUpdateDeal } from "api/deals/service";
import { useUpdateCalculus } from "api/calculuses/service";

const UserDefinedTab = forwardRef(({id, data, activeColor, module}, ref) => {
  const { t } = useTranslation();
  // State for each data type - initialize with existing data
  const [currencyData, setCurrencyData] = useState(data?.currency || {});
  const [datetimeData, setDatetimeData] = useState(data?.datetime || {});
  const [numericData, setNumericData] = useState(data?.numeric || {});
  const [percentageData, setPercentageData] = useState(data?.percentage || {});
  const [textData, setTextData] = useState(data?.text || {});

  const updateInventoryMutation = useUpdateInventory();
  const updateDealMutation = useUpdateDeal();
  const updateCalculus = useUpdateCalculus();

  // Update state when inventoryData changes
  useEffect(() => {
    if (data) {
      setCurrencyData(data?.currency || {});
      setDatetimeData(data?.datetime || {});
      setNumericData(data?.numeric || {});
      setPercentageData(data?.percentage || {});
      setTextData(data?.text || {});
    }
  }, [data]);

  const handleInputChange = (type, fieldName, value) => {
    switch(type) {
      case 'currency':
        setCurrencyData(prev => ({...prev, [fieldName]: value}));
        break;
      case 'datetime':
        setDatetimeData(prev => ({...prev, [fieldName]: value}));
        break;
      case 'numeric':
        setNumericData(prev => ({...prev, [fieldName]: value}));
        break;
      case 'percentage':
        setPercentageData(prev => ({...prev, [fieldName]: value}));
        break;
      case 'text':
        setTextData(prev => ({...prev, [fieldName]: value}));
        break;
      default:
        break;
    }
  };

  // Expose save function to parent component
  useImperativeHandle(ref, () => ({
    handleSave: async () => {
      const updateData = {
        user_defineds: {
          currency: currencyData,
          datetime: datetimeData,
          numeric: numericData,
          percentage: percentageData,
          text: textData
        }
      };
      if (module === "deals") {
        updateDealMutation.mutate({ id: id, data: updateData });
      } 
      if (module === "accounts") {
        updateCalculus.mutate({ id: id, data: updateData });
      }
      if (module === "inventory") {
        updateInventoryMutation.mutate({ id: id, data: updateData });
      }
      return { success: true };
    }
  }));

  // Render dynamic fields for a data type
  const renderFields = (data, type, title) => {
    const fieldNames = Object.keys(data);
    
    return (
      <div style={{width: 'calc(20% - 10px)'}}>
        <div className="card">
          <div className="card-header font-weight-bold text-uppercase">{title}</div>
          <div className="card-body">
            {fieldNames.length === 0 ? (
              <div className="text-muted text-center">{t('userDefinedTab.noFieldsAvailable')}</div>
            ) : (
              fieldNames.map((fieldName, index) => (
                <div className="form-group" key={index}>
                  <label>{fieldName}</label>
                  <input 
                    style={{borderRadius: 0,fontSize: '14px'}} 
                    type={type === 'datetime' ? 'datetime-local' : 
                          type === 'numeric' || type === 'percentage' ? 'number' : 'text'}
                    className="form-control" 
                    placeholder={t(`userDefinedTab.enter${type.charAt(0).toUpperCase() + type.slice(1)}Value`)}
                    min={type === 'percentage' ? '0' : undefined}
                    max={type === 'percentage' ? '100' : undefined}
                    value={data[fieldName] || ''}
                    onChange={(e) => handleInputChange(type, fieldName, e.target.value)}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="row" style={{gap:10}}>
      {/* CURRENCY Box */}
      {renderFields(currencyData, 'currency', t('userDefinedTab.currency'))}

      {/* DATETIME Box */}
      {renderFields(datetimeData, 'datetime', t('userDefinedTab.datetime'))}

      {/* NUMERIC Box */}
      {renderFields(numericData, 'numeric', t('userDefinedTab.numeric'))}

      {/* PERCENTAGE Box */}
      {renderFields(percentageData, 'percentage', t('userDefinedTab.percentage'))}

      {/* TEXT Box */}
      {renderFields(textData, 'text', t('userDefinedTab.text'))}
    </div>
  );
});

export default UserDefinedTab; 